import { useState, useCallback } from 'react';
import {
  type TestStatus,
  type StepStatus,
  type IceServerArray,
} from '@types';
import { fetchLiveIceServers, getTwilioToken } from '@services/Common';
import { getCurrentUser, useSessionStore } from '@features/auth';
import { writeSuccessLog } from '@lib/logger/logSuccess.tsx';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { useTranslation } from 'react-i18next';

interface TwilioTokenApiResponse {
  success: boolean;
  status: number;
  data?: {
    agentID?: string;
    token?: string;
  };
}

export interface UseInternetDiagnosticReturn {
  internetStatus: TestStatus;
  isSystemOnline: boolean;
  turnUdpStatus: StepStatus;
  turnUdpValue: string;
  turnTcpStatus: StepStatus;
  turnTcpValue: string;
  bandwidthStatus: StepStatus;
  bandwidthValue: string;
  isTesting: boolean;
  startAutoTest: () => Promise<void>;
  udpError: string | null;
  tcpError: string | null;
}

interface IceServersWrapper {
  iceServers: IceServerArray;
}

async function testRealTwilioVoiceBandwidth (
  _twilioToken: string,
  _iceServers: IceServerArray
): Promise<{ speedMbps: number; isExcellent: boolean }> {
  return await new Promise((resolve) => {

    const testUrl = `https://speed.cloudflare.com/__down?bytes=5000000&_cb=${Date.now()}`;
    const startTime = performance.now();

    fetch(testUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Fetch failed with status ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (reader == null) {
          throw new Error('ReadableStream not supported or empty body');
        }

        let receivedBytes = 0;
        const readChunk = async (): Promise<void> => {
          const { done, value } = await reader.read();
          if (done) {
            return;
          }
          if (value !== undefined && value !== null) {
            receivedBytes += value.length;
          }
          await readChunk();
        };

        await readChunk();

        const endTime = performance.now();

        const durationInSeconds = (endTime - startTime) / 1000;
        const totalBits = receivedBytes * 8;
        const bps = totalBits / durationInSeconds;
        const calculatedMbps = bps / 1000000;

        const speedMbps = Math.round(calculatedMbps * 100) / 100;

        resolve({
          speedMbps: speedMbps > 0 ? speedMbps : 0.5,
          isExcellent: speedMbps >= 10
        });
      })
      .catch((error) => {
        const safeError = error instanceof Error ? error : new Error(String(error));
        const errorContext: Record<string, unknown> = {
          message: safeError.message,
          name: safeError.name,
          stack: safeError.stack
        };
        void writeErrorLog(`Real bandwidth test failed user: ${getCurrentUser()?.userId}`, 'testInternetViewModel', errorContext);
        resolve({ speedMbps: 0, isExcellent: false });
      });
  });
}

export function useTestInternetViewModel (): UseInternetDiagnosticReturn {
  const agentId = useSessionStore().currentUser?.userId;

  const [internetStatus, setInternetStatus] = useState<TestStatus>('idle');

  const [turnUdpStatus, setTurnUdpStatus] = useState<StepStatus>('idle');
  const [turnUdpValue, setTurnUdpValue] = useState('');
  const [udpError, setUdpError] = useState<string | null>(null);

  const [turnTcpStatus, setTurnTcpStatus] = useState<StepStatus>('idle');
  const [turnTcpValue, setTurnTcpValue] = useState('');
  const [tcpError, setTcpError] = useState<string | null>(null);

  const [bandwidthStatus, setBandwidthStatus] = useState<StepStatus>('idle');
  const [bandwidthValue, setBandwidthValue] = useState('');
  const [isSystemOnline] = useState(typeof window !== 'undefined' ? navigator.onLine : true);
  const [isFetchingTokens, setIsFetchingTokens] = useState(false);
  const { t } = useTranslation();

  const testTransport = useCallback(async (iceServers: IceServerArray, label: string): Promise<number> => {
    return await new Promise((resolve, reject) => {
      try {
        const pc = new RTCPeerConnection({ iceServers });
        pc.createDataChannel('test-channel');
        const startTime = performance.now();

        const timeout = setTimeout(() => {
          pc.close();
          reject(new Error(`Timeout gathering candidates for ${label}`));
        }, 30000);

        let hasFoundValidCandidate = false;

        pc.onicecandidate = (event) => {
          if (event.candidate == null) {
            if (!hasFoundValidCandidate) {
              clearTimeout(timeout);
              pc.close();
              reject(new Error(`No connectivity found for ${label}`));
            }
            return;
          }

          const candidateStr = event.candidate.candidate;
          const isUdpValid = label === 'UDP' && (candidateStr.includes('srflx') || candidateStr.includes('relay'));
          const isTcpValid = label === 'TCP' && candidateStr.includes('relay');

          if (isUdpValid || isTcpValid) {
            hasFoundValidCandidate = true;
            clearTimeout(timeout);
            pc.close();

            const duration = Math.round(performance.now() - startTime);
            resolve(duration);
          }
        };

        pc.oniceconnectionstatechange = () => {
          if (pc.iceConnectionState === 'failed') {
            clearTimeout(timeout);
            pc.close();
            reject(new Error(`ICE Connection failed directly on ${label}`));
          }
        };

        pc.createOffer()
          .then(async (offer) => {
            await pc.setLocalDescription(offer);
          })
          .catch((err: unknown) => {
            clearTimeout(timeout);
            pc.close();
            reject(err instanceof Error ? err : new Error(String(err)));
          });
      } catch (err: unknown) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }, []);

  const runInternetTest = useCallback(async (liveServers: IceServerArray): Promise<void> => {
    setInternetStatus('running');

    setTurnUdpStatus('running');
    setTurnUdpValue('running');
    setUdpError(null);

    setTurnTcpStatus('idle');
    setTurnTcpValue('');
    setTcpError(null);

    let serversArray: IceServerArray = [];

    if (Array.isArray(liveServers)) {
      serversArray = liveServers;
    } else if (
      liveServers &&
      typeof liveServers === 'object' &&
      'iceServers' in liveServers
    ) {
      const wrapper = liveServers as unknown as IceServersWrapper;
      if (Array.isArray(wrapper.iceServers)) {
        serversArray = wrapper.iceServers;
      }
    }

    // 1. UDP
    let udpRtt = 0;
    let finalUdpStatus: StepStatus = 'failed';
    let finalUdpValue = 'Blocked';
    let caughtUdpError: string | null = null;

    const udpIceServers = serversArray.map((server) => {
      const urlsProperty = server.urls ?? [];
      const urlsArray = typeof urlsProperty === 'string' ? [urlsProperty] : urlsProperty;
      const filteredUrls = urlsArray.filter((url) => !url.includes('?transport=tcp'));
      return { ...server, urls: filteredUrls };
    }).filter((server) => server.urls.length > 0);

    try {
      udpRtt = await testTransport(udpIceServers, 'UDP');
      finalUdpStatus = 'success';
      finalUdpValue = `${udpRtt} ms`;
    } catch (error: unknown) {
      console.error('Real UDP Test Failed:', error);
      caughtUdpError = error instanceof Error ? error.message : 'Unknown UDP error';
    }
    setTurnUdpStatus(finalUdpStatus);
    setTurnUdpValue(finalUdpValue);
    setUdpError(caughtUdpError);

    // 2. TCP
    setTurnTcpStatus('running');
    setTurnTcpValue('running');

    let tcpRtt = 0;
    let finalTcpStatus: StepStatus = 'failed';
    let finalTcpValue = 'Blocked';
    let caughtTcpError: string | null = null;

    try {
      const tcpIceServers = serversArray.map((server) => {
        const urlsProperty = server.urls ?? [];
        const urlsArray = typeof urlsProperty === 'string' ? [urlsProperty] : urlsProperty;
        const filteredUrls = urlsArray.filter((url) =>
          url.startsWith('stun:') || url.includes('?transport=tcp')
        );
        return { ...server, urls: filteredUrls };
      }).filter((server) => server.urls.length > 0);

      tcpRtt = await testTransport(tcpIceServers, 'TCP');
      finalTcpStatus = 'success';
      finalTcpValue = `${tcpRtt} ms`;
    } catch (error: unknown) {
      console.error('Real TCP Test Failed:', error);
      caughtTcpError = error instanceof Error ? error.message : 'Unknown TCP error';
    }
    setTurnTcpStatus(finalTcpStatus);
    setTurnTcpValue(finalTcpValue);
    setTcpError(caughtTcpError);

    // 3. Bandwidth
    setBandwidthStatus('running');
    setBandwidthValue('running');

    let finalInternetStatus: TestStatus = 'red';
    let finalBandwidthStatus: StepStatus = 'failed';
    let finalBandwidthValue = t('agentLayout.testDialog.internetMode.connectionStatuses.poor');

    const identity = getCurrentUser()?.userId;
    let twilioTokenStr = '';
    let speedMbps = 0;

    try {
      const rawResponse = await getTwilioToken(identity);
      const twilioTokenResponse = rawResponse as TwilioTokenApiResponse;

      if (twilioTokenResponse?.data != null) {
        twilioTokenStr = twilioTokenResponse.data.token ?? '';
      }

      const bandwidthResult = await testRealTwilioVoiceBandwidth(twilioTokenStr, serversArray);
      speedMbps = bandwidthResult.speedMbps;

    } catch (bandwidthError) {
      console.error('Error in bandwidth measurement section:', bandwidthError);
    }

    if (udpRtt === 0 && tcpRtt === 0) {
      finalInternetStatus = 'red';
      finalBandwidthStatus = 'failed';
      finalBandwidthValue = t('agentLayout.testDialog.internetMode.connectionStatuses.noConnected');
    } else {
      let label: string;

      if (speedMbps >= 10) {
        finalBandwidthStatus = 'success';
        label = t('agentLayout.testDialog.internetMode.connectionStatuses.excellent');
      } else if (speedMbps >= 3) {
        finalBandwidthStatus = 'success';
        label = t('agentLayout.testDialog.internetMode.connectionStatuses.good');
      } else {
        finalBandwidthStatus = 'failed';
        label = t('agentLayout.testDialog.internetMode.connectionStatuses.poor');
      }

      const bestRtt = udpRtt > 0 ? udpRtt : tcpRtt;
      if (bestRtt < 250 && speedMbps >= 10 && udpRtt > 0) {
        finalInternetStatus = 'green';
      } else if (bestRtt < 500 && speedMbps >= 3) {
        finalInternetStatus = 'yellow';
      } else {
        finalInternetStatus = 'red';
      }

      finalBandwidthValue = `${label} ${speedMbps} Mbps`;
    }

    setInternetStatus(finalInternetStatus);
    setBandwidthStatus(finalBandwidthStatus);
    setBandwidthValue(finalBandwidthValue);

    const logPayload = {
      agentId: agentId ?? 'unknown_agent',
      results: {
        udp: { status: finalUdpStatus, value: finalUdpValue, error: caughtUdpError },
        tcp: { status: finalTcpStatus, value: finalTcpValue, error: caughtTcpError },
        bandwidth: { status: finalBandwidthStatus, value: finalBandwidthValue },
      },
    };

    if (finalInternetStatus === 'green' || finalInternetStatus === 'yellow') {
      void writeSuccessLog(`Diagnostic completed user: ${getCurrentUser()?.userId}`, 'AgentInternetTest', logPayload);
    } else {
      const errorReasons: string[] = [];
      if (caughtUdpError != null) {
        errorReasons.push(`UDP: ${caughtUdpError}`);
      }
      if (caughtTcpError != null) {
        errorReasons.push(`TCP: ${caughtTcpError}`);
      }

      const errorMessageSummary = errorReasons.length > 0
        ? `Diagnostic Failed - ${errorReasons.join(' | ')}`
        : 'Diagnostic Complete - Poor Connection Detected';

      void writeErrorLog(`${errorMessageSummary}-user: ${getCurrentUser()?.userId}}`, 'AgentInternetTest', logPayload);
    }
  }, [agentId, testTransport, t]);

  const startAutoTest = useCallback(async (): Promise<void> => {
    setIsFetchingTokens(true);

    setTurnUdpStatus('idle');
    setTurnUdpValue('');
    setUdpError(null);

    setTurnTcpStatus('idle');
    setTurnTcpValue('');
    setTcpError(null);

    setBandwidthStatus('idle');
    setBandwidthValue('');

    try {
      const freshServers = await fetchLiveIceServers();
      if (freshServers.success && (freshServers.data != null)) {
        await runInternetTest(freshServers.data);
      } else {
        throw new Error(((freshServers.error?.message) != null) ? freshServers.error.message : 'Failed to fetch tokens from response');
      }
    } catch (error) {
      await writeErrorLog(`Error initializing diagnostic user: ${getCurrentUser()?.userId}`, 'TestInternet.startAutoTest', { error });
    } finally {
      setIsFetchingTokens(false);
    }
  }, [fetchLiveIceServers, runInternetTest]);

  return {
    internetStatus,
    isSystemOnline,
    turnUdpStatus,
    turnUdpValue,
    turnTcpStatus,
    turnTcpValue,
    bandwidthStatus,
    bandwidthValue,
    isTesting: isFetchingTokens || internetStatus === 'running',
    startAutoTest,
    udpError,
    tcpError,
  };
}